/**
 * Report Review session — legacy `?reportreview=true&bldngId=` parity (DEF-78).
 *
 * v2 opens a dedicated report-review window that stores flags in sessionStorage
 * and lands on Floors & Tenants with the SSI report-review version selected.
 * Orders-app deep links read the same keys via `identity.service.ts`.
 */

const REPORT_REVIEW_SESSION_KEY = 'reportReview';
const REPORT_REVIEW_BUILDING_ID_KEY = 'reportReviewBldgId';
const REPORT_REVIEW_BUILDING_CODE_KEY = 'reportReviewBldgNum';
const REPORT_REVIEW_BUILDING_NAME_KEY = 'reportReviewBldgName';

/** True when URL carries legacy report-review mode (`reportreview=1|true`). */
export function isReportReviewSearchParams(searchParams: URLSearchParams): boolean {
  const flag = searchParams.get('reportreview');
  return flag === '1' || flag === 'true';
}

/** Building id from `bldngId` query param (legacy casing). */
export function reportReviewBuildingIdFromSearch(
  searchParams: URLSearchParams,
): number | null {
  const raw = searchParams.get('bldngId') ?? searchParams.get('bldngid');
  if (!raw) return null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/** Persist report-review context for cross-app deep links (legacy sessionStorage keys). */
export function activateReportReviewSession(input: {
  buildingId: number;
  buildingCode?: string | null;
  buildingName?: string | null;
}): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(REPORT_REVIEW_SESSION_KEY, 'true');
  sessionStorage.setItem(REPORT_REVIEW_BUILDING_ID_KEY, String(input.buildingId));
  if (input.buildingCode?.trim()) {
    sessionStorage.setItem(REPORT_REVIEW_BUILDING_CODE_KEY, input.buildingCode.trim());
  }
  if (input.buildingName?.trim()) {
    sessionStorage.setItem(REPORT_REVIEW_BUILDING_NAME_KEY, input.buildingName.trim());
  }
}

/** Clears report-review session keys (e.g. when leaving review mode). */
export function clearReportReviewSession(): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(REPORT_REVIEW_SESSION_KEY);
  sessionStorage.removeItem(REPORT_REVIEW_BUILDING_ID_KEY);
  sessionStorage.removeItem(REPORT_REVIEW_BUILDING_CODE_KEY);
  sessionStorage.removeItem(REPORT_REVIEW_BUILDING_NAME_KEY);
}

/** True when sessionStorage indicates an active report-review window. */
export function isReportReviewSessionActive(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(REPORT_REVIEW_SESSION_KEY) === 'true';
}

/** Building id persisted by {@link activateReportReviewSession}. */
export function reportReviewBuildingIdFromSession(): number | null {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(REPORT_REVIEW_BUILDING_ID_KEY);
  if (!raw) return null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * Keep Report Review nav on the selected building (legacy popup + Cindy 08-21).
 * Drawing Vault has no listing-scope query, so it deep-links that property.
 */
export function reportReviewScopedPath(
  path: string,
  buildingId: number | null,
): string {
  if (buildingId == null) return path;
  const pathname = path.split('?')[0] ?? path;
  if (pathname === '/drawing-vault' || pathname.startsWith('/drawing-vault/')) {
    return `/drawing-vault/${buildingId}`;
  }
  const qs = new URLSearchParams({ reportreview: '1', bldngId: String(buildingId) });
  return `${pathname}?${qs.toString()}`;
}

/**
 * Opens Report Review in a dedicated window (DEF-427 / legacy `openReportReviewWindow`).
 *
 * Legacy opens a full-screen popup and stores report-review session keys on that
 * window. The new window then loads the CM-perspective property detail URL.
 *
 * DEF-434: the session keys must NOT be written when the popup opens. `sessionStorage`
 * is copied into the popup at `window.open` time and is otherwise shared with every
 * page in the opener's tab, so seeding here put the SSI's own main window into CM
 * perspective and stripped seven admin menus from it. The popup does not need the
 * seed — `PropertyDetailPage` re-activates from the `reportreview=1` URL param on
 * boot. Only the same-tab fallback below needs it, and that path also navigates, so
 * the opener never ends up CM-chromed while still sitting on the property listing.
 *
 * @returns The opened window handle, or `null` when the browser blocked the popup.
 */
export function openReportReviewWindow(href: string, buildingId: number): Window | null {
  if (typeof window === 'undefined') return null;
  const features = [
    `width=${window.screen.width}`,
    `height=${window.screen.height}`,
    'menubar=no',
    'toolbar=no',
    'location=yes',
    'resizable=yes',
    'scrollbars=yes',
  ].join(',');
  const win = window.open(href, `reportReview-${buildingId}`, features);
  if (win) return win;

  activateReportReviewSession({ buildingId });
  window.location.assign(href);
  return null;
}
